(() => {
  // Parse the base field label out of a line-item property name, stripping any "Item N · " prefix.
  const NS_RE = /^Item\s+\d+\s+·\s+/;
  function baseLabel(propName) {
    const m = /^properties\[(.+)\]$/.exec(propName || '');
    if (!m) return null;
    return m[1].replace(NS_RE, '');
  }

  // Canvas live preview: draws template-defined zones (normalized 0-1 coords) over the mockup.
  function initCanvasPreview(root) {
    const canvas = root.querySelector('[data-personalizer-canvas]');
    const configEl = root.querySelector('[data-preview-config]');
    if (!canvas || !configEl) return null;
    let config;
    try { config = JSON.parse(configEl.textContent); } catch { return null; }
    const preview = config.preview || {};
    const ctx = canvas.getContext('2d');
    const mockup = new Image();
    mockup.crossOrigin = 'anonymous';
    let customerPhoto = null;
    let values = {};
    let bindings = null; // AbortController for the currently bound field group

    const drawCover = (img, zx, zy, zw, zh, fit) => {
      const zone = { x: zx * canvas.width, y: zy * canvas.height, w: zw * canvas.width, h: zh * canvas.height };
      const scale = (fit === 'contain' ? Math.min : Math.max)(zone.w / img.width, zone.h / img.height);
      const dw = img.width * scale, dh = img.height * scale;
      ctx.save();
      ctx.beginPath(); ctx.rect(zone.x, zone.y, zone.w, zone.h); ctx.clip();
      ctx.drawImage(img, zone.x + (zone.w - dw) / 2, zone.y + (zone.h - dh) / 2, dw, dh);
      ctx.restore();
    };

    const render = () => {
      if (!mockup.complete || !mockup.naturalWidth) return;
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const cssWidth = canvas.parentElement.clientWidth || 600;
      const ratio = mockup.naturalHeight / mockup.naturalWidth;
      canvas.width = Math.round(cssWidth * dpr);
      canvas.height = Math.round(cssWidth * ratio * dpr);
      canvas.style.height = 'auto';
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      try { ctx.drawImage(mockup, 0, 0, canvas.width, canvas.height); } catch { return; }
      if (customerPhoto && preview.photoZone) {
        const z = preview.photoZone;
        drawCover(customerPhoto, z.x, z.y, z.w, z.h, z.fit);
      }
      (preview.textZones || []).forEach(zone => {
        const text = values[zone.field];
        if (!text) return;
        let size = zone.size * canvas.width;
        ctx.font = `${size}px ${zone.font || 'serif'}`;
        const maxWidth = (zone.maxWidth || 1) * canvas.width;
        while (size > 8 && ctx.measureText(text).width > maxWidth) { size -= 1; ctx.font = `${size}px ${zone.font || 'serif'}`; }
        ctx.fillStyle = zone.color || '#000';
        ctx.textAlign = zone.align || 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(text, zone.x * canvas.width, zone.y * canvas.height, maxWidth);
      });
    };

    mockup.onload = render;
    mockup.onerror = () => { canvas.closest('.magic-personalizer__canvas-wrap')?.setAttribute('hidden', ''); };
    mockup.src = config.mockup;
    window.addEventListener('resize', render, { passive: true });
    if ('ResizeObserver' in window && canvas.parentElement) {
      let rafId;
      new ResizeObserver(() => { cancelAnimationFrame(rafId); rafId = requestAnimationFrame(render); }).observe(canvas.parentElement);
    }

    const setText = (field, value) => { values[field] = value; render(); };
    const setPhoto = (img) => { customerPhoto = img; render(); };

    // Point the preview at one field group (accordion item): bind its text inputs by base label
    // and load its photo if one is already selected. Aborts the previous group's listeners.
    const rebindTo = (scopeEl) => {
      bindings?.abort();
      bindings = new AbortController();
      const signal = bindings.signal;
      values = {}; customerPhoto = null;
      const controls = scopeEl ? [...scopeEl.querySelectorAll('[name^="properties["]')] : [];
      (preview.textZones || []).forEach(zone => {
        const input = controls.find(el => el.type !== 'radio' && el.type !== 'file' && baseLabel(el.name) === zone.field);
        if (!input) return;
        input.addEventListener('input', () => setText(zone.field, input.value), { signal });
        if (input.value) values[zone.field] = input.value;
      });
      const photo = scopeEl?.querySelector('[data-personalizer-photo]');
      const file = photo?.files?.[0];
      if (file && /^image\//.test(file.type)) {
        const reader = new FileReader();
        reader.onload = e => { const img = new Image(); img.onload = () => setPhoto(img); img.src = e.target.result; };
        reader.readAsDataURL(file);
      } else { render(); }
    };

    // Hover zoom-and-pan on fine pointers: magnify the part of the preview under the cursor.
    // The frame clips the overflow; the zoom origin follows the cursor. Touch devices are untouched.
    const frame = canvas.parentElement;
    if (frame && window.matchMedia('(pointer:fine)').matches) {
      const zoomScale = 2.4;
      frame.classList.add('is-zoomable');
      frame.addEventListener('mousemove', event => {
        const rect = frame.getBoundingClientRect();
        const x = Math.min(100, Math.max(0, ((event.clientX - rect.left) / rect.width) * 100));
        const y = Math.min(100, Math.max(0, ((event.clientY - rect.top) / rect.height) * 100));
        canvas.style.transformOrigin = `${x}% ${y}%`;
        canvas.style.transform = `scale(${zoomScale})`;
      });
      frame.addEventListener('mouseleave', () => { canvas.style.transform = ''; });
    }

    return { setText, setPhoto, rebindTo };
  }

  // Shared: show/hide fields gated on a variant option (field.showWhen), across all cloned groups.
  function bindConditional(root, productForm) {
    const update = () => {
      root.querySelectorAll('[data-personalizer-conditional]').forEach(wrapper => {
        const selected = productForm?.querySelector(`input[name="options[${CSS.escape(wrapper.dataset.optionName)}]"]:checked`)?.value;
        const visible = selected === wrapper.dataset.optionValue;
        wrapper.hidden = !visible;
        wrapper.querySelectorAll('input, textarea, select').forEach(control => { control.disabled = !visible; });
      });
    };
    update();
    productForm?.addEventListener('change', event => { if (event.target.matches('input[name^="options["]')) update(); });
    return update;
  }

  // Shared: reflect the chosen image-select swatch label next to its heading.
  function bindSwatches(root) {
    const updateCurrent = radio => {
      const current = radio.closest('.magic-personalizer__swatch-group')?.querySelector('[data-swatch-current]');
      if (current) current.textContent = radio.dataset.swatchLabel || '';
    };
    root.querySelectorAll('[data-swatch-radio]:checked').forEach(updateCurrent);
    root.addEventListener('change', event => { if (event.target.matches('[data-swatch-radio]')) updateCurrent(event.target); });
  }

  // Shared: validate a photo file input; returns an error string or ''.
  function checkPhoto(input, maxMb) {
    const file = input.files?.[0];
    if (!file) return '';
    if (file.size > maxMb * 1024 * 1024) { input.value = ''; return `Photo must be smaller than ${maxMb} MB.`; }
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) { input.value = ''; return 'Please upload a JPG, PNG or WEBP image.'; }
    const fileName = input.closest('.magic-personalizer__upload')?.querySelector('[data-file-name]');
    if (fileName) fileName.textContent = file.name;
    return '';
  }

  // Spec path: clone one field group per pack item, namespace properties, and drive an accordion
  // with a single shared live preview for the active item.
  function initGroupPersonalizer(root, template) {
    const canvasPreview = initCanvasPreview(root);
    const itemsContainer = root.querySelector('[data-personalizer-items]');
    const scopeEl = root.querySelector('[data-personalizer-scope]');
    const marker = root.querySelector('[data-personalization-marker]');
    const error = root.querySelector('[data-personalizer-error]');
    const maxMb = Number(root.dataset.maxFileMb || 10);
    const packOption = root.dataset.packOptionName || 'Pack';
    const productForm = document.getElementById(root.querySelector('[form]')?.getAttribute('form')) || root.closest('form');
    if (!itemsContainer || !template) return;

    const updateConditional = bindConditional(root, productForm);
    bindSwatches(root);

    const packSize = () => {
      const val = productForm?.querySelector(`input[name="options[${CSS.escape(packOption)}]"]:checked`)?.value;
      const n = val && /(\d+)/.exec(val);
      return n ? Math.max(1, parseInt(n[1], 10)) : 1;
    };
    const mode = () => (scopeEl?.querySelector('[data-personalizer-scope-toggle]:checked')?.value === 'each' ? 'each' : 'same');

    const items = []; // { el, body } accordion items in order
    let activeIndex = 0;

    const namespaceItem = (body, i, useEach) => {
      body.querySelectorAll('[name^="properties["]').forEach(el => {
        const base = baseLabel(el.name);
        if (!base) return;
        el.name = useEach ? `properties[Item ${i} · ${base}]` : `properties[${base}]`;
        // JS owns validation across collapsed items, so strip native required (avoids validating hidden fields).
        if (el.dataset.origRequired === undefined) el.dataset.origRequired = el.required ? 'true' : 'false';
        el.required = false;
      });
    };

    const buildItem = () => {
      const wrap = document.createElement('div');
      wrap.className = 'magic-personalizer__item';
      wrap.dataset.personalizerItem = '';
      const header = document.createElement('button');
      header.type = 'button';
      header.className = 'magic-personalizer__item-header';
      header.innerHTML = '<span class="magic-personalizer__item-title"></span><span class="magic-personalizer__item-status" data-item-status></span>';
      const body = document.createElement('div');
      body.className = 'magic-personalizer__item-body';
      body.appendChild(template.content.cloneNode(true));
      wrap.append(header, body);
      header.addEventListener('click', () => setActive(items.findIndex(it => it.el === wrap)));
      return { el: wrap, body };
    };

    const setActive = (i) => {
      if (i < 0 || i >= items.length) i = 0;
      activeIndex = i;
      items.forEach((it, idx) => it.el.classList.toggle('is-open', idx === i));
      canvasPreview?.rebindTo(items[i]?.body);
    };

    const render = () => {
      const size = packSize();
      const useEach = size > 1 && mode() === 'each';
      const count = useEach ? size : 1;

      // Toggle visibility: only relevant when the pack holds more than one item.
      if (scopeEl) scopeEl.hidden = size <= 1;

      // Grow/shrink the item list, preserving existing groups (and their entered values).
      while (items.length < count) { const it = buildItem(); items.push(it); itemsContainer.appendChild(it.el); }
      while (items.length > count) { const it = items.pop(); it.el.remove(); }

      items.forEach((it, idx) => {
        namespaceItem(it.body, idx + 1, useEach);
        it.el.classList.toggle('magic-personalizer__item--single', count === 1);
        const title = it.el.querySelector('.magic-personalizer__item-title');
        if (title) title.textContent = `Item ${idx + 1}`;
      });
      if (marker) marker.value = useEach ? `Per-item ×${size}` : `Same ×${size}`;
      updateConditional(); // re-apply variant-gated field visibility to freshly cloned groups
      setActive(Math.min(activeIndex, count - 1));
    };

    // Replace the server-rendered fallback group with JS-managed accordion items.
    itemsContainer.innerHTML = '';
    render();

    scopeEl?.addEventListener('change', event => { if (event.target.matches('[data-personalizer-scope-toggle]')) render(); });
    productForm?.addEventListener('change', event => { if (event.target.matches(`input[name="options[${CSS.escape(packOption)}]"]`)) render(); });

    // Photo validation + live preview for the active item (event-delegated to cover clones).
    root.addEventListener('change', event => {
      const input = event.target;
      if (!input.matches?.('[data-personalizer-photo]')) return;
      const msg = checkPhoto(input, maxMb);
      if (error) error.textContent = msg;
      const body = input.closest('.magic-personalizer__item-body');
      const idx = items.findIndex(it => it.body === body);
      if (!msg && idx === activeIndex) canvasPreview?.rebindTo(items[idx].body);
    });

    // JS validation across every item on submit. Runs in the CAPTURE phase so it fires before the
    // theme's bubble-phase AJAX add-to-cart (assets/theme.js); on failure it stops that handler.
    // Collapsed items are display:none, so native validation cannot reach them.
    productForm?.addEventListener('submit', event => {
      if (yieldsToCustomily(root)) { disableControls(root); return; }
      for (let idx = 0; idx < items.length; idx++) {
        const body = items[idx].body;
        const controls = [...body.querySelectorAll('[name^="properties["]')].filter(el => el.dataset.origRequired === 'true' && !el.disabled);
        const seenRadio = new Set();
        for (const el of controls) {
          let bad = false;
          if (el.type === 'radio') {
            if (seenRadio.has(el.name)) continue;
            seenRadio.add(el.name);
            bad = !body.querySelector(`input[name="${CSS.escape(el.name)}"]:checked`);
          } else {
            bad = !el.value || (el.type === 'file' && !el.files?.length);
          }
          if (bad) {
            event.preventDefault();
            event.stopImmediatePropagation();
            setActive(idx);
            if (error) error.textContent = items.length > 1 ? `Please complete Item ${idx + 1}.` : 'Please complete the personalization.';
            el.focus?.();
            root.scrollIntoView({ behavior: 'smooth', block: 'center' });
            return;
          }
        }
      }
    }, true);
  }

  // Legacy path: static name/date/message/photo fields for products tagged 'personalized' without a spec.
  function initLegacyPersonalizer(root) {
    const canvasPreview = initCanvasPreview(root);
    const productForm = document.getElementById(root.querySelector('[form]')?.getAttribute('form')) || root.closest('form');
    canvasPreview?.rebindTo(root);
    bindConditional(root, productForm);
    bindSwatches(root);
    const name = root.querySelector('[data-personalizer-name]');
    const date = root.querySelector('[data-personalizer-date]');
    const message = root.querySelector('[data-personalizer-message]');
    const previewPhoto = root.querySelector('[data-preview-photo]');
    const error = root.querySelector('[data-personalizer-error]');
    const maxMb = Number(root.dataset.maxFileMb || 10);

    name?.addEventListener('input', () => { const t = root.querySelector('[data-preview-name]'); if (t) t.textContent = name.value || name.placeholder; });
    date?.addEventListener('input', () => {
      const value = date.value ? new Intl.DateTimeFormat(document.documentElement.lang || 'en', { year: 'numeric', month: 'long', day: 'numeric' }).format(new Date(`${date.value}T00:00:00`)) : '';
      const t = root.querySelector('[data-preview-date]'); if (t) t.textContent = value || 'Your special date';
    });
    message?.addEventListener('input', () => {
      const p = root.querySelector('[data-preview-message]'); if (p) p.textContent = message.value || message.placeholder;
      const count = root.querySelector('[data-character-count]'); if (count) count.textContent = message.value.length;
    });
    root.querySelectorAll('[data-personalizer-photo]').forEach((photo, index) => photo.addEventListener('change', () => {
      const msg = checkPhoto(photo, maxMb); if (error) error.textContent = msg; if (msg) return;
      const file = photo.files?.[0]; if (!file) return;
      const reader = new FileReader();
      reader.onload = event => {
        if (index === 0 && previewPhoto) { previewPhoto.src = event.target.result; previewPhoto.classList.add('visible'); }
        if (index === 0 && canvasPreview) { const img = new Image(); img.onload = () => canvasPreview.setPhoto(img); img.src = event.target.result; }
      };
      reader.readAsDataURL(file);
    }));
  }

  // A product personalized with Customily must not also show the LeN fields (e.g. a product
  // duplicated from one that had a personalizer stamp). Customily mounts #customily-options in
  // the product form; once it does, hide this panel and disable its controls so they neither
  // block Add to cart nor add properties to the line item.
  const yieldsToCustomily = root => root.dataset.yieldTo === 'customily';
  const disableControls = root => root.querySelectorAll('input, select, textarea').forEach(control => { control.disabled = true; });
  function watchCustomily(root) {
    const form = document.getElementById(root.querySelector('[form]')?.getAttribute('form')) || root.closest('form');
    if (!form) return;
    const check = () => {
      if (!form.querySelector('#customily-options')) return false;
      root.dataset.yieldTo = 'customily';
      root.hidden = true;
      root.style.display = 'none'; // theme CSS sets display on .magic-personalizer
      disableControls(root);
      return true;
    };
    if (check()) return;
    const observer = new MutationObserver(() => { if (check()) observer.disconnect(); });
    observer.observe(form, { childList: true, subtree: true });
  }

  function initPersonalizer(root) {
    if (root.dataset.initialized === 'true') return;
    root.dataset.initialized = 'true';
    const template = root.querySelector('template[data-personalizer-group]');
    if (template) initGroupPersonalizer(root, template);
    else initLegacyPersonalizer(root);
    watchCustomily(root);
  }

  function initAll(scope = document) { scope.querySelectorAll('[data-magic-personalizer]').forEach(initPersonalizer); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll()); else initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
